import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check, Instagram, Mail, Phone } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { submitContactRequest } from "@/server/contact.functions";
import { Turnstile } from "@/components/Turnstile";
import { turnstileActivo } from "@/lib/turnstile";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: "Consultar disponibilidad · SUSIEDAD" },
      {
        name: "description",
        content:
          "Consulta fecha y presupuesto para bodas, eventos e instituciones. Desde Málaga para toda España.",
      },
    ],
  }),
  component: ContactPage,
});

type ContactForm = {
  name: string;
  email: string;
  phone: string;
  event_type: "WEDDING" | "CLUB" | "PRIVATE_EVENT" | "CORPORATE" | "OTHER";
  event_date: string;
  approximate_time: string;
  location: string;
  attendees: string;
  budget: string;
  message: string;
  consent: boolean;
  website: string;
};
const services = ["DJ", "Sonido", "Iluminación", "Confeti", "Fuego frío", "CO₂"];

function ContactPage() {
  const [sent, setSent] = useState(false);
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ContactForm>({
    defaultValues: { event_type: "WEDDING", consent: false, website: "" },
  });
  async function submit(values: ContactForm) {
    try {
      await submitContactRequest({
        data: { ...values, requested_services: selectedServices, turnstileToken },
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "No se pudo enviar la solicitud");
    }
  }
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-12 lg:px-8 lg:pt-20">
        <div className="grid gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-20">
          <aside>
            <p className="eyebrow text-primary">Contacto · Disponibilidad</p>
            <h1 className="display-lg mt-5">HABLEMOS DE TU FECHA.</h1>
            <p className="mt-7 max-w-lg text-xl text-muted-foreground">
              Cuéntame qué estás preparando. Recibirás una respuesta personal con disponibilidad y
              próximos pasos.
            </p>
            <div className="mt-10 space-y-4 border-t border-border pt-8">
              <a
                className="flex items-center gap-3 hover:text-primary"
                href="mailto:lasusiedad@gmail.com"
              >
                <Mail className="size-4 text-primary" /> lasusiedad@gmail.com
              </a>
              <a className="flex items-center gap-3 hover:text-primary" href="tel:+34633199708">
                <Phone className="size-4 text-primary" /> 633 199 708
              </a>
              <a
                className="flex items-center gap-3 hover:text-primary"
                href="https://instagram.com/la_susiedad"
                target="_blank"
                rel="noreferrer"
              >
                <Instagram className="size-4 text-primary" /> @la_susiedad
              </a>
            </div>
            <div className="mt-10 bg-onyx p-6 text-bone">
              <p className="eyebrow !text-neon">Base</p>
              <p className="mt-3 font-display text-3xl">MÁLAGA</p>
              <p className="mt-2 text-bone/60">Servicio disponible en toda España.</p>
            </div>
          </aside>
          {sent ? (
            <section className="grid min-h-[32rem] place-items-center border-t-4 border-primary bg-bone p-10 text-center">
              <div>
                <span className="mx-auto inline-grid size-14 place-items-center rounded-full bg-primary text-white">
                  <Check className="size-6" />
                </span>
                <h2 className="mt-6 font-display text-5xl">SOLICITUD RECIBIDA.</h2>
                <p className="mt-4 text-muted-foreground">
                  Ya está guardada en el CRM. SUSIEDAD contactará contigo personalmente.
                </p>
              </div>
            </section>
          ) : (
            <form
              className="grid gap-5 border-t-4 border-primary bg-bone p-5 sm:grid-cols-2 sm:p-8"
              onSubmit={handleSubmit(submit)}
              noValidate
            >
              <Input label="Nombre*" error={errors.name?.message}>
                <input
                  className="field"
                  {...register("name", {
                    required: "Introduce tu nombre",
                    minLength: { value: 2, message: "Nombre demasiado corto" },
                  })}
                />
              </Input>
              <Input label="Email*" error={errors.email?.message}>
                <input
                  className="field"
                  type="email"
                  {...register("email", { required: "Introduce tu email" })}
                />
              </Input>
              <Input label="Teléfono*" error={errors.phone?.message}>
                <input
                  className="field"
                  type="tel"
                  {...register("phone", {
                    required: "Introduce tu teléfono",
                    minLength: { value: 6, message: "Teléfono no válido" },
                  })}
                />
              </Input>
              <Input label="Tipo de evento">
                <select className="field" {...register("event_type")}>
                  <option value="WEDDING">Boda</option>
                  <option value="CLUB">Club</option>
                  <option value="PRIVATE_EVENT">Evento privado</option>
                  <option value="CORPORATE">Corporativo</option>
                  <option value="OTHER">Otro</option>
                </select>
              </Input>
              <Input label="Fecha">
                <input className="field" type="date" {...register("event_date")} />
              </Input>
              <Input label="Hora aproximada">
                <input className="field" type="time" {...register("approximate_time")} />
              </Input>
              <Input label="Lugar">
                <input
                  className="field"
                  placeholder="Ciudad, finca o sala"
                  {...register("location")}
                />
              </Input>
              <Input label="Asistentes">
                <input className="field" type="number" min="1" {...register("attendees")} />
              </Input>
              <div className="sm:col-span-2">
                <p className="eyebrow mb-3 text-foreground">Servicios</p>
                <div className="flex flex-wrap gap-2">
                  {services.map((service) => (
                    <button
                      key={service}
                      type="button"
                      className={selectedServices.includes(service) ? "btn-primary" : "btn-quiet"}
                      onClick={() =>
                        setSelectedServices((current) =>
                          current.includes(service)
                            ? current.filter((item) => item !== service)
                            : [...current, service],
                        )
                      }
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>
              <Input label="Presupuesto orientativo">
                <input className="field" placeholder="Opcional" {...register("budget")} />
              </Input>
              <span />
              <div className="sm:col-span-2">
                <Input label="Cuéntame el evento">
                  <textarea className="field min-h-36" maxLength={2000} {...register("message")} />
                </Input>
              </div>
              <input className="hidden" tabIndex={-1} autoComplete="off" {...register("website")} />
              <label className="flex items-start gap-3 text-sm sm:col-span-2">
                <input
                  className="mt-1"
                  type="checkbox"
                  {...register("consent", { required: "Debes aceptar la política de privacidad" })}
                />
                <span>
                  Acepto la{" "}
                  <a className="underline" href="/privacidad">
                    política de privacidad
                  </a>{" "}
                  y el tratamiento de mis datos para responder a esta solicitud.
                  {errors.consent && (
                    <span className="block text-red-700">{errors.consent.message}</span>
                  )}
                </span>
              </label>
              <div className="sm:col-span-2">
                {/* Sin clave de sitio (desarrollo local) no se dibuja nada y el
                    servidor deja pasar la solicitud. Con clave, el botón espera
                    a que la verificación se resuelva sola. */}
                <Turnstile onToken={setTurnstileToken} />
                <button
                  className="btn-primary mt-4 w-full sm:w-auto"
                  disabled={isSubmitting || (turnstileActivo && !turnstileToken)}
                >
                  {isSubmitting
                    ? "Enviando…"
                    : turnstileActivo && !turnstileToken
                      ? "Verificando…"
                      : "Enviar solicitud"}
                  <ArrowUpRight className="size-4" />
                </button>
              </div>
            </form>
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Input({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="eyebrow mb-2 block text-foreground">{label}</span>
      {children}
      {error && <span className="mt-1 block text-sm text-red-700">{error}</span>}
    </label>
  );
}
